import { ReactNode, useMemo, useRef, useState } from 'react';
import { CADENCE_NAMES, CadenceKind } from '../../data/chorales/derive';
import type { BreathName } from '../../data/chorales/corpusTypes';
import { instanceOf } from '../../data/corpus';
import { pathById } from '../../data';
import {
  CADENCE_WORDS,
  CorpusName,
  ModeFilter,
  Tally,
  breath,
  cadenceKinds,
  commonChords,
  endings,
  exampleKey,
  landings,
  nextChords,
  pathCounts,
  totals,
} from '../../data/observatory';
import { formatRoman } from '../../music/roman';
import { usePrefs, useWidth } from '../hooks';
import { CHORD_WORDS } from '../copy';
import { SONATA_ENTRIES, VARIATION_ENTRIES } from '../../data/chorales/corpusIndex';
import { Excerpts } from '../components/Excerpts';

/* ---------- Plain words ---------- */

const LANDING_WORDS: Record<'major' | 'minor', Record<string, string>> = {
  major: {
    I: 'Home',
    V: 'The bright side',
    vi: 'The shadow',
    IV: 'The soft side',
    ii: 'A step up, in minor',
    iii: 'A third up, in minor',
    v: 'The bright side, darkened',
    bVII: 'A step down',
    II: 'A step up, in major',
  },
  minor: {
    i: 'Home',
    III: 'Into the light',
    iv: 'The soft side',
    VII: 'A step down, in major',
    v: 'The minor dominant',
    VI: 'A sixth up, in major',
    IV: 'The soft side, brightened',
    I: 'Home, in major',
    ii: 'A step up',
  },
};

const LANDING_NAMES: Record<string, string> = {
  I: 'tonic',
  i: 'tonic',
  V: 'dominant key',
  v: 'minor dominant',
  vi: 'relative minor',
  III: 'relative major',
  IV: 'subdominant key',
  iv: 'subdominant key',
  ii: 'supertonic key',
  iii: 'mediant key',
  VI: 'submediant key',
  VII: 'subtonic key',
  bVII: 'flat seventh',
  II: 'major supertonic',
};


const BREATH_WORDS: Record<BreathName, { word: string; name: string; color: string }> = {
  home: { word: 'Phrases that arrive home', name: 'Authentic cadence in the home key', color: 'var(--series-1)' },
  question: { word: 'Phrases that end on a question', name: 'Half cadence', color: 'var(--series-2)' },
  away: { word: 'Phrases that arrive somewhere else', name: 'Authentic cadence in another key', color: 'var(--series-3)' },
  deceptive: { word: 'Phrases where the ground gives way', name: 'Deceptive cadence', color: 'var(--muted)' },
};

const pct = (x: number) => `${x < 0.01 && x > 0 ? '<1' : Math.round(x * 100)}%`;

/* ---------- Bars ---------- */

interface BarRow {
  id: string;
  label: ReactNode;
  sub?: ReactNode;
  count: number;
  share: number;
}

function BarList({ rows, selected, onPick, caption }: { rows: BarRow[]; selected?: string; onPick: (id: string) => void; caption: string }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="bars" role="group" aria-label={caption}>
      {rows.map((r) => (
        <button
          key={r.id}
          className="bar-row"
          aria-pressed={selected === r.id}
          onClick={() => onPick(r.id)}
          aria-label={`${typeof r.label === 'string' ? r.label : r.id}: ${r.count}, ${pct(r.share)}. Play examples.`}
        >
          <span className="bar-label">
            {r.label}
            {r.sub && <small>{r.sub}</small>}
          </span>
          <span className="bar-track" aria-hidden="true">
            <i style={{ width: `${(r.count / max) * 100}%` }} />
          </span>
          <span className="bar-value">
            <b>{r.count.toLocaleString('en')}</b> <span>{pct(r.share)}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

/** A tally list and the excerpts of the picked row, played as soon as it is picked. */
function Counted({
  tallies,
  label,
  sub,
  caption,
  initial,
  note,
  extra,
}: {
  tallies: Tally[];
  label: (t: Tally) => ReactNode;
  sub?: (t: Tally) => ReactNode;
  caption: string;
  initial?: string;
  note?: string;
  /** Shown next to the picked row's heading (e.g. a link). */
  extra?: (t: Tally) => ReactNode;
}) {
  const [picked, setPicked] = useState<string | undefined>(initial);
  const [play, setPlay] = useState(0);
  const current = tallies.find((t) => t.id === picked) ?? tallies[0];
  const items = useMemo(() => (current ? current.refs.map((r) => instanceOf(r)) : []), [current]);
  return (
    <div className="counted">
      <BarList
        caption={caption}
        rows={tallies.map((t) => ({ id: t.id, label: label(t), sub: sub?.(t), count: t.count, share: t.share }))}
        selected={current?.id}
        onPick={(id) => {
          setPicked(id);
          setPlay((p) => p + 1);
        }}
      />
      {current && (
        <div className="counted-excerpts">
          <span className="label">
            {label(current)} · {current.count.toLocaleString('en')}
            {extra && <> · {extra(current)}</>}
          </span>
          <Excerpts items={items} autoplay={play} shown={5} note={note} />
        </div>
      )}
    </div>
  );
}

/* ---------- Breath chart ---------- */

const SERIES: BreathName[] = ['home', 'question', 'away'];

function BreathChart({ mode, corpus, picked, onPick }: { mode: ModeFilter; corpus: CorpusName; picked: BreathName; onPick: (b: BreathName) => void }) {
  const data = breath(mode, corpus);
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const W = Math.max(280, width || 600);
  const H = 240;
  const m = { l: 40, r: 14, t: 12, b: 34 };
  const n = data.home.mean.length;
  const top = 70;
  const x = (i: number) => m.l + ((i + 0.5) / n) * (W - m.l - m.r);
  const y = (v: number) => m.t + (1 - v / top) * (H - m.t - m.b);
  const index = (clientX: number) => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return null;
    const px = ((clientX - box.left) / box.width) * W;
    return Math.max(0, Math.min(n - 1, Math.round(((px - m.l) / (W - m.l - m.r)) * n - 0.5)));
  };
  const hovered = hover ?? null;
  return (
    <div className="breath" ref={ref}>
      <div className="legend" role="group" aria-label="Which phrases">
        {SERIES.map((s) => (
          <button key={s} aria-pressed={picked === s} onClick={() => onPick(s)}>
            <svg width="18" height="8" aria-hidden="true">
              <line x1="1" x2="17" y1="4" y2="4" stroke={BREATH_WORDS[s].color} strokeWidth="2.5" strokeLinecap="round" />
            </svg>
            {BREATH_WORDS[s].word} <span className="muted">({data[s].n.toLocaleString('en')})</span>
          </button>
        ))}
      </div>
      <div className="chart-wrap">
        <svg
          ref={svgRef}
          width={W}
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label="Average tension through a phrase, by how the phrase ends"
          tabIndex={0}
          onPointerMove={(e) => setHover(index(e.clientX))}
          onPointerLeave={() => setHover(null)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') setHover((h) => Math.min(n - 1, (h ?? -1) + 1));
            if (e.key === 'ArrowLeft') setHover((h) => Math.max(0, (h ?? n) - 1));
          }}
          onBlur={() => setHover(null)}
        >
          {[0, 20, 40, 60].map((v) => (
            <g key={v}>
              <line x1={m.l} x2={W - m.r} y1={y(v)} y2={y(v)} className="c-grid" />
              <text x={m.l - 8} y={y(v) + 4} textAnchor="end" className="c-tick">
                {v}
              </text>
            </g>
          ))}
          <text x={m.l} y={H - 10} className="c-tick">
            first note
          </text>
          <text x={(m.l + W - m.r) / 2} y={H - 10} textAnchor="middle" className="c-tick">
            middle of the phrase
          </text>
          <text x={W - m.r} y={H - 10} textAnchor="end" className="c-tick">
            {corpus === 'bach' ? 'fermata' : 'phrase end'}
          </text>
          {SERIES.map((s) => {
            const pts = data[s].mean.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
            const last = data[s].mean[n - 1];
            return (
              <g key={s} opacity={picked === s ? 1 : 0.55}>
                <path d={pts} fill="none" stroke={BREATH_WORDS[s].color} strokeWidth={picked === s ? 2.5 : 2} strokeLinejoin="round" strokeLinecap="round" />
                <circle cx={x(n - 1)} cy={y(last)} r={4} fill={BREATH_WORDS[s].color} stroke="var(--surface)" strokeWidth={2} />
              </g>
            );
          })}
          {hovered !== null && (
            <g pointerEvents="none">
              <line x1={x(hovered)} x2={x(hovered)} y1={m.t} y2={H - m.b} className="c-cross" />
              {SERIES.map((s) => (
                <circle key={s} cx={x(hovered)} cy={y(data[s].mean[hovered])} r={4} fill={BREATH_WORDS[s].color} stroke="var(--surface)" strokeWidth={2} />
              ))}
            </g>
          )}
        </svg>
        {hovered !== null && (
          <div className="c-tip" style={{ left: x(hovered) + 250 > W ? Math.max(0, x(hovered) - 246) : x(hovered) + 10 }} role="status">
            <div className="muted">{Math.round(((hovered + 0.5) / n) * 100)}% through the phrase</div>
            {SERIES.map((s) => (
              <div key={s} className="c-tip-row">
                <svg width="14" height="6" aria-hidden="true">
                  <line x1="1" x2="13" y1="3" y2="3" stroke={BREATH_WORDS[s].color} strokeWidth="2.5" strokeLinecap="round" />
                </svg>
                <b>{data[s].mean[hovered]}</b> <span className="muted">{BREATH_WORDS[s].word.toLowerCase()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <details className="table-view">
        <summary>Show as a table</summary>
        <table>
          <thead>
            <tr>
              <th>Through the phrase</th>
              {SERIES.map((s) => (
                <th key={s}>{BREATH_WORDS[s].word}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.home.mean.map((_, i) => (
              <tr key={i}>
                <td>{Math.round(((i + 0.5) / n) * 100)}%</td>
                {SERIES.map((s) => (
                  <td key={s}>{data[s].mean[i]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

/* ---------- Page ---------- */

export function Observatory() {
  const { prefs } = usePrefs();
  const { depth } = prefs;
  const [mode, setMode] = useState<ModeFilter>('all');
  const [corpus, setCorpus] = useState<CorpusName>(() => {
    const h = window.location.hash;
    if (SONATA_ENTRIES.length > 0 && h.includes('mozart')) return 'mozart';
    if (VARIATION_ENTRIES.length > 0 && h.includes('variations')) return 'variations';
    return 'bach';
  });
  const bach = corpus === 'bach';
  const sets = corpus === 'variations';
  const unit = bach ? 'chorale' : sets ? 'set' : 'movement';
  const composerWord = bach ? 'Bach' : sets ? 'Mozart and Beethoven' : 'Mozart';
  const [localMode, setLocalMode] = useState<'major' | 'minor'>('major');
  const [chord, setChord] = useState<Record<'major' | 'minor', string>>({ major: 'V', minor: 'V' });
  const [breathPick, setBreathPick] = useState<BreathName>('home');
  const [breathPlay, setBreathPlay] = useState(0);
  const [fact, setFact] = useState<'home' | 'picardy' | 'firstAway'>('picardy');
  const [factPlay, setFactPlay] = useState(0);

  const t = useMemo(() => totals(mode, corpus), [mode, corpus]);
  const kinds = useMemo(() => cadenceKinds(mode, corpus), [mode, corpus]);
  const landMajor = useMemo(() => (mode === 'minor' ? [] : landings('major', corpus).slice(0, 8)), [mode, corpus]);
  const landMinor = useMemo(() => (mode === 'major' ? [] : landings('minor', corpus).slice(0, 8)), [mode, corpus]);
  const end = useMemo(() => endings(mode, corpus), [mode, corpus]);
  const chips = useMemo(() => commonChords(localMode, mode, 12, corpus), [localMode, mode, corpus]);
  const next = useMemo(() => nextChords(chord[localMode], localMode, mode, corpus).slice(0, 8), [chord, localMode, mode, corpus]);
  const paths = useMemo(() => pathCounts(mode, corpus), [mode, corpus]);
  const breathItems = useMemo(() => breath(mode, corpus)[breathPick].typical.map((r) => instanceOf(r)), [mode, corpus, breathPick]);

  const kindLabel = (k: string) => (depth >= 2 ? CADENCE_NAMES[k as CadenceKind] : CADENCE_WORDS[k as CadenceKind]);
  const kindSub = (k: string) => (depth >= 2 ? CADENCE_WORDS[k as CadenceKind] : undefined);
  const landingLabel = (rel: string, home: 'major' | 'minor') => {
    const word = LANDING_WORDS[home][rel] ?? 'Further away';
    return depth >= 2 ? `${formatRoman(rel)} · ${LANDING_NAMES[rel.replace(/^[#b]/, '')] ?? 'another key'}` : word;
  };
  const chordLabel = (fam: string) => (depth >= 2 ? formatRoman(fam) : CHORD_WORDS[fam] ?? formatRoman(fam));
  const chordSub = (fam: string) => (depth >= 2 ? CHORD_WORDS[fam] : formatRoman(fam));

  const facts: Array<{ id: typeof fact; n: number; of: number; text: string }> = [
    { id: 'home', n: end.home.length, of: end.total, text: `${unit}s end their last phrase at home, with the bass falling from the dominant` },
    { id: 'picardy', n: end.picardy.length, of: end.minorTotal, text: `minor-key ${unit}s end on a major chord: the last chord turns to light` },
    { id: 'firstAway', n: end.firstAway.length, of: end.total, text: `${unit}s end their very first phrase away from home` },
  ];
  // The chosen fact may count nothing in this corpus or mode (no minor-key movement ends major): fall back to one that does.
  const shownFact = facts.find((f) => f.id === fact && f.n > 0 && f.of > 0)?.id ?? facts.find((f) => f.n > 0 && f.of > 0)?.id ?? fact;
  const factItems = useMemo(() => ({ home: end.home, picardy: end.picardy, firstAway: end.firstAway })[shownFact].map((r) => instanceOf(r)), [shownFact, end]);

  return (
    <div className="observatory">
      <header className="page-head">
        <span className="label">Statistics</span>
        <h1>{bach ? 'What Bach does, counted' : sets ? 'Variations, counted' : 'What Mozart does, counted'}</h1>
        <p>
          Every number here is made of moments you can hear. Pick a row in any chart and the moments it counts play one after another, all moved to C major or A minor
          so only the shape is left.
        </p>
        <p className="hint">
          Counted from {t.chorales} {bach ? 'Bach chorales' : sets ? 'sets of variations by Mozart and Beethoven' : 'movements of Mozart’s piano sonatas'}, by the same
          rules as their pages. Switch the music below to compare a Baroque hymn with a Classical sonata or variation set.
        </p>
      </header>
      <nav className="jump" aria-label="Sections on this page">
        {[
          ['obs-ends', 'How phrases end'],
          ['obs-land', 'Where phrases land'],
          ['obs-last', 'Last and first phrases'],
          ['obs-next', 'What comes next'],
          ['obs-breath', 'The shape of a phrase'],
          ['obs-paths', 'Patterns, counted'],
        ].map(([id, label]) => (
          <a key={id} href={`#observatory`} onClick={(e) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>
            {label}
          </a>
        ))}
      </nav>

      <div className="obs-filter" role="group" aria-label="What to count">
        <span className="label">Count</span>
        {(SONATA_ENTRIES.length > 0 || VARIATION_ENTRIES.length > 0) && (
          <div className="seg" role="group" aria-label="Which music">
            <button aria-pressed={bach} onClick={() => setCorpus('bach')}>
              Bach chorales
            </button>
            {SONATA_ENTRIES.length > 0 && (
              <button aria-pressed={corpus === 'mozart'} onClick={() => setCorpus('mozart')}>
                Mozart sonatas
              </button>
            )}
            {VARIATION_ENTRIES.length > 0 && (
              <button aria-pressed={sets} onClick={() => setCorpus('variations')}>
                Variations
              </button>
            )}
          </div>
        )}
        <div className="seg" role="group" aria-label="Which keys">
          {(['all', 'major', 'minor'] as ModeFilter[]).map((m) => (
            <button key={m} aria-pressed={mode === m} onClick={() => setMode(m)}>
              {m === 'all' ? 'All' : m === 'major' ? 'Major' : 'Minor'}
              <span className="wide-only">{m === 'all' ? ` ${unit}s` : ` key`}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="tiles">
        {[
          [bach ? 'chorales' : sets ? 'sets' : 'movements', t.chorales],
          ['phrases', t.phrases],
          ['chord labels', t.chords],
          ['patterns found', t.paths],
        ].map(([label, value]) => (
          <div key={label as string} className="tile">
            <span className="tile-value">{(value as number).toLocaleString('en')}</span>
            <span className="tile-label">{label}</span>
          </div>
        ))}
      </div>

      <section className="panel obs-section" id="obs-ends">
        <h2>How phrases end</h2>
        <p className="listen">
          {bach
            ? `Every phrase of a chorale stops at a fermata. This is how those ${t.phrases.toLocaleString('en')} stops sound. Which kind do you hear most?`
            : sets
              ? `Every phrase of a theme or variation ends somewhere (the phrases come from TAVERN). This is how those ${t.phrases.toLocaleString('en')} endings sound: the same few cadences, heard over and over in new clothes.`
              : `Every phrase of a movement ends somewhere (the phrase endings come from the DCML annotations). This is how those ${t.phrases.toLocaleString('en')} endings sound.`}
        </p>
        <Counted key={`kinds-${corpus}`} tallies={kinds} caption="Kinds of phrase ending" label={(x) => kindLabel(x.id)} sub={(x) => kindSub(x.id)} initial="pac" />
      </section>

      <section className="panel obs-section" id="obs-land">
        <h2>Where phrases land</h2>
        <p className="listen">
          A phrase can stop at home or in a neighbouring key. Keys are named from home, so every {unit} can be counted together; the example key is for a {unit} in C
          major or A minor.
        </p>
        <div className="obs-two">
          {landMajor.length > 0 && (
            <div>
              <span className="label">In major-key {unit}s</span>
              <Counted key={`maj-${corpus}`} tallies={landMajor} caption="Where phrases land in major keys" label={(x) => landingLabel(x.id, 'major')} sub={(x) => `in C major: ${exampleKey(x.id, 'major')}`} initial="V" />
            </div>
          )}
          {landMinor.length > 0 && (
            <div>
              <span className="label">In minor-key {unit}s</span>
              <Counted key={`min-${corpus}`} tallies={landMinor} caption="Where phrases land in minor keys" label={(x) => landingLabel(x.id, 'minor')} sub={(x) => `in A minor: ${exampleKey(x.id, 'minor')}`} initial="III" />
            </div>
          )}
        </div>
      </section>

      <section className="panel obs-section" id="obs-last">
        <h2>Last and first phrases</h2>
        <div className="facts">
          {facts
            .filter((f) => f.of > 0)
            .map((f) => (
              <button
                key={f.id}
                className="fact"
                aria-pressed={shownFact === f.id}
                onClick={() => {
                  setFact(f.id);
                  setFactPlay((p) => p + 1);
                }}
              >
                <span className="tile-value">
                  {f.n.toLocaleString('en')} <span className="of">of {f.of.toLocaleString('en')}</span>
                </span>
                <span>{f.text}</span>
              </button>
            ))}
        </div>
        <Excerpts items={factItems} autoplay={factPlay} shown={5} />
      </section>

      <section className="panel obs-section" id="obs-next">
        <h2>What comes next</h2>
        <p className="listen">Choose a chord. The bars show what {composerWord} {sets ? 'put' : 'puts'} after it (the next different chord, in the same key), and play the moments of change.</p>
        <div className="obs-row">
          <div className="seg" role="group" aria-label="Keys">
            <button aria-pressed={localMode === 'major'} onClick={() => setLocalMode('major')}>
              In major keys
            </button>
            <button aria-pressed={localMode === 'minor'} onClick={() => setLocalMode('minor')}>
              In minor keys
            </button>
          </div>
        </div>
        <div className="chips chord-chips" role="group" aria-label="From which chord">
          {chips.map((c) => (
            <button key={c.family} className="chip" aria-pressed={chord[localMode] === c.family} onClick={() => setChord((x) => ({ ...x, [localMode]: c.family }))}>
              <b>{formatRoman(c.family)}</b> {depth < 2 && CHORD_WORDS[c.family] ? <span className="muted">{CHORD_WORDS[c.family]}</span> : null}
            </button>
          ))}
        </div>
        <span className="label">
          After {formatRoman(chord[localMode])}
          {CHORD_WORDS[chord[localMode]] ? ` (${CHORD_WORDS[chord[localMode]]})` : ''}
        </span>
        <Counted key={`${localMode}-${chord[localMode]}-${mode}-${corpus}`} tallies={next} caption="Next chords" label={(x) => chordLabel(x.id)} sub={(x) => chordSub(x.id)} />
      </section>

      <section className="panel obs-section" id="obs-breath">
        <h2>The shape of a phrase</h2>
        <p className="listen">
          The average pull of the harmony from the first note of a phrase to its {bach ? 'fermata' : 'close'}. Phrases that arrive let go at the very end; phrases that end on a question keep
          holding. Pick a kind of ending to hear the phrases closest to its average.
        </p>
        <BreathChart
          mode={mode}
          corpus={corpus}
          picked={breathPick}
          onPick={(b) => {
            setBreathPick(b);
            setBreathPlay((p) => p + 1);
          }}
        />
        <span className="label">
          Typical: {BREATH_WORDS[breathPick].word.toLowerCase()}
          {depth >= 2 ? ` (${BREATH_WORDS[breathPick].name.toLowerCase()})` : ''}
        </span>
        <Excerpts items={breathItems} autoplay={breathPlay} shown={5} />
        <p className="muted" style={{ fontSize: '0.8rem' }}>
          Pull is this site’s tension model (0 = at rest), averaged over {breath(mode, corpus).home.n + breath(mode, corpus).question.n + breath(mode, corpus).away.n} phrases of
          at least one bar. Phrases that end deceptively ({breath(mode, corpus).deceptive.n}) are too few to average.
        </p>
      </section>

      <section className="panel obs-section" id="obs-paths">
        <h2>Patterns, counted</h2>
        <p className="listen">The patterns the rules find inside the {unit}s. Open a pattern to hear it on its own.</p>
        <Counted
          key={`paths-${corpus}`}
          tallies={paths}
          caption="Patterns found"
          label={(x) => {
            const p = pathById(x.id);
            return p ? (depth >= 2 ? (x.id === 'deceptive-cadence' ? 'Deceptive progression (anywhere)' : p.name) : p.feeling) : x.id;
          }}
          extra={(x) => <a href={`#path.${x.id}`}>open the path</a>}
        />
      </section>

      <p className="foot">
        Counts come from the harmonic analyses of When in Rome, read by this site’s rules (see Method): they are only as good as the analyses and the rules. BWV 244.17,
        which repeats 244.15 a semitone lower, is not counted twice. Mozart phrase endings come from the DCML annotations (CC BY-NC-SA 4.0); the variation sets, their
        phrases and their harmony from TAVERN (CC BY-SA 4.0).
      </p>
    </div>
  );
}
